import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Suspense } from 'react';
import { AppShell } from '@/components/layout/app-shell';
import { SnapshotBanner } from '@/components/ui/snapshot-banner';
import { ModalProvider } from '@/lib/modals/modal-context';
import { EditorModalHost } from '@/components/modals/editor-modal-host';
import { CommandPaletteProvider } from '@/components/ui/workspace/command-palette';
import { ToastProvider } from '@/components/ui/workspace/toast';
import { NotFound } from '@/components/layout/not-found';

/**
 * Root route (T-0096 / E10).
 *
 * Renders the global chrome (AppShell — left sidebar + top breadcrumb bar)
 * and an `<Outlet />` for the active child route. The `/login` route opts
 * out of the chrome (no auth context, no sidebar context) so we
 * short-circuit on that path and render the outlet bare.
 *
 * The {@link SnapshotBanner} from T-0095 mounts unconditionally — it
 * self-hides when no snapshot is active (`useSnapshotMode().active === null`).
 * Routes that open a snapshot scope (T-0101 owns the URL-driven entries)
 * call `enterSnapshotMode()` / `exitSnapshotMode()` on the shared store.
 *
 * React Query devtools mount only in DEV so they don't ship in the
 * production bundle.
 *
 * Two complementary modal coordinators sit inside the provider:
 *   - T-0113's {@link ModalProvider} wraps the whole tree and owns
 *     the imperative single-modal slot used by the upload / override /
 *     re-eval tray + the citation-source modal compositional path.
 *   - T-0114's {@link EditorModalHost} subscribes to `data-open-*`
 *     click delegation to launch the rubric / domains / checkpoints /
 *     widgets / parse editor modals. The two paths don't collide —
 *     T-0113's slot is imperative (useModal().openModal(...)), T-0114's
 *     is event-delegated, and either can close the other if a user
 *     stacks them (the ModalProvider slot replaces on open).
 *
 * T-0141 mounts the two app-wide workspace providers here, once:
 * {@link ToastProvider} (the toast-with-undo region) and
 * {@link CommandPaletteProvider} (the Cmd/Ctrl+K palette). Screens add
 * commands with `useRegisterCommands` and raise toasts with `useToast`;
 * the top bar's trigger opens the palette. The login route gets the
 * providers too, so the tree shape does not change with the path.
 */
export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootComponent() {
  const { pathname } = useLocation();
  // The login screen is the only route that pre-dates an auth session, so
  // it shouldn't render the workbench chrome around it.
  // `/l` is the no-login page a secure link opens (T-0207): never the chrome.
  const isBare =
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/l' ||
    pathname === '/l/';

  return (
    <ModalProvider>
      <ToastProvider>
        <CommandPaletteProvider disableShortcut={isBare}>
          <SnapshotBanner />
          {isBare ? (
            <Outlet />
          ) : (
            <AppShell>
              <Outlet />
            </AppShell>
          )}
        </CommandPaletteProvider>
      </ToastProvider>
      {/*
        T-0114 — Global editor-modal host. Subscribes to `data-open-*`
        click triggers (rubric / domains / checkpoints / widgets /
        parse) and renders the matching editor modal. Bare login route
        skips chrome but the host has nothing to do there either, so we
        only mount it in the authenticated shell.
      */}
      {isBare ? null : <EditorModalHost />}
      {import.meta.env.DEV ? (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </Suspense>
      ) : null}
    </ModalProvider>
  );
}
